<template>
  <div>
    <BaseSimpleLayout class="mccs-settings">
      <template #title>MCCS設定項目</template>
      <template #button-area>
        <el-input
          v-model="searchText"
          size="large"
          placeholder="検索"
          @input="searchData"
        />
        <ScreenPermissionGate code="MCCS_SETTINGS_ITEM-BTN_EDIT">
          <el-button
            class="mccs-settings-button-update"
            type="success"
            size="large"
            @click="updateSettings"
            :disabled="hasError"
          >
            更新
          </el-button>
        </ScreenPermissionGate>
      </template>
      <template #main>
        <el-table
          ref="tableRef"
          :data="tableData"
          border
          :max-height="tableHeight"
          @row-click="toggleRowEdit"
          :row-class-name="tableRowClassName"
        >
          <el-table-column prop="property" label="項目" width="200" sortable>
            <template #default="scope">
              <template v-if="editingRowId === scope.row.id">
                <el-tooltip
                  :content="rowErrors[scope.row.id]?.property"
                  :disabled="!rowErrors[scope.row.id]?.property"
                  transition="slide-fade"
                  effect="error"
                >
                  <el-input
                    v-model="scope.row.property"
                    size="small"
                    :class="{ 'is-error': rowErrors[scope.row.id]?.property }"
                  />
                </el-tooltip>
              </template>
              <template v-else>
                <span>{{ scope.row.property }}</span>
              </template>
            </template>
          </el-table-column>
          <el-table-column prop="type" label="入力形式" width="140">
            <template #default="scope">
              <el-select
                v-if="editingRowId === scope.row.id"
                v-model="scope.row.type"
                tabindex="-1"
                size="small"
                :ref="setSelectRef(scope.row.id)"
                @visible-change="
                  (visible: boolean) =>
                    handleSelectVisibleChange(visible, scope.row.id)
                "
              >
                <el-option label="文字" :value="1" />
                <el-option label="数値" :value="2" />
                <el-option label="DYNAMIC" :value="3" />
              </el-select>
              <span v-else>
                {{ getInputTypeLabel(scope.row.type) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="説明" min-width="300">
            <template #default="scope">
              <el-input
                v-if="editingRowId === scope.row.id"
                type="textarea"
                v-model="scope.row.description"
                :rows="3"
                size="small"
              />
              <span v-else>
                {{ scope.row.description }}
              </span>
            </template>
          </el-table-column>
          <!-- el-table-column must be a direct child of el-table. Wrapping it in ScreenPermissionGate keeps the column from rendering. -->
          <el-table-column
            v-if="canDeleteSetting"
            label="削除"
            width="80"
            align="center"
          >
            <template #default="scope">
              <el-checkbox
                :checked="scope.row.toDelete"
                :key="scope.row.toDelete"
                @change="
                  (checked: boolean) => handleDeleteChange(checked, scope.row)
                "
              />
            </template>
          </el-table-column>
        </el-table>
        <div class="add-row-container">
          <ScreenPermissionGate code="MCCS_SETTINGS_ITEM-BTN_EDIT">
            <el-button type="primary" size="large" @click.stop="addNewRow">
              追加
            </el-button>
          </ScreenPermissionGate>
        </div>
      </template>
    </BaseSimpleLayout>

    <!-- Delete Confirmation Dialog -->
    <el-dialog v-model="deleteDialogVisible" title="削除確認" width="400px">
      <span>
        <b>ファイナンス別設定も削除されますが、よろしいでしょうか。</b>
      </span>
      <template #footer>
        <el-button size="large" @click="cancelDelete">キャンセル</el-button>
        <el-button size="large" type="danger" @click="confirmDelete">
          削除
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
  import { ref, nextTick, onMounted, onBeforeUnmount, computed } from 'vue';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import { ElLoading } from 'element-plus';
  import { useStore } from 'vuex';
  import { hasScreenCodePermission } from '@/libs/auth';
  import { success, error } from '@/libs/notification';

  const MCCS_SETTINGS_EDIT_CODE = 'MCCS_SETTINGS_ITEM-BTN_EDIT';
  const canDeleteSetting = computed(() =>
    hasScreenCodePermission('MCCS_SETTINGS_ITEM-BTN_DELETE')
  );

  interface MCCSSettingsData {
    id: number | string;
    property: string;
    type: number;
    description: string | null;
    toDelete: boolean;
    canEdit: boolean;
  }

  function getInputTypeLabel(type: number) {
    const labels: Record<number, string> = {
      1: '文字',
      2: '数値',
      3: 'DYNAMIC'
    };
    return labels[type] || type.toString();
  }

  const store = useStore();
  const tableData = computed(() => {
    const existingRows = [...store.state.mccsProperties.listData]
      .filter((row) => !String(row.id).startsWith('new_'))
      .sort((a, b) => {
        return a.property.localeCompare(b.property);
      });

    const newRows = [...store.state.mccsProperties.listData].filter((row) =>
      String(row.id).startsWith('new_')
    );

    return [...existingRows, ...newRows];
  });
  const tableHeight = ref(600);
  const searchText = ref('');
  const newRowCounter = ref(1);
  const tableRef = ref();
  const editingRowId = ref<string | number | null>(0);
  const selectRefs = ref<
    Record<
      string | number,
      InstanceType<(typeof import('element-plus'))['ElSelect']> | null
    >
  >({});
  const openSelectRowId = ref<string | number | null>(null);
  let originalData: MCCSSettingsData[] = [];
  const rowErrors = ref<
    Record<string | number, Partial<Record<keyof MCCSSettingsData, string>>>
  >({});
  const deleteDialogVisible = ref(false);
  const rowToDelete = ref<MCCSSettingsData | null>(null);

  function validateRow(
    row: MCCSSettingsData,
    allRows: MCCSSettingsData[]
  ): Partial<Record<keyof MCCSSettingsData, string>> {
    if (!row) return {};
    const errors: Partial<Record<keyof MCCSSettingsData, string>> = {};
    if (
      !String(row.id).includes('new_') &&
      !row.toDelete &&
      !row.property.trim()
    )
      errors.property = '項目は必須です';
    const duplicate = allRows.filter(
      (r) =>
        row.property.trim() && r.property === row.property && r.id !== row.id
    );
    if (duplicate.length > 0) errors.property = '項目が重複しています';
    return errors;
  }

  const hasError = computed(() =>
    Object.values(rowErrors.value).some(
      (err) => err && Object.keys(err).length > 0
    )
  );
  const initialize = async () => {
    await store.dispatch('mccsProperties/fetch');
    tableHeight.value = window.innerHeight - 120;
    // Save original data for reset and comparison
    originalData = store.state.mccsProperties.listData.map(
      (row: MCCSSettingsData) => ({ ...row })
    );
  };

  const searchData = () => {
    if (!searchText.value) {
      // Reset tableData from store
      store.commit(
        'mccsProperties/setListData',
        originalData.map((row: MCCSSettingsData) => ({ ...row }))
      );
      return;
    }
    const filtered = originalData.filter((item: MCCSSettingsData) => {
      return (
        item.property.includes(searchText.value) ||
        item.description?.includes(searchText.value)
      );
    });
    store.commit('mccsProperties/setListData', filtered);
  };

  const toggleRowEdit = (
    row: MCCSSettingsData,
    _col: unknown,
    event: Event
  ) => {
    event.stopPropagation();
    if (!hasScreenCodePermission(MCCS_SETTINGS_EDIT_CODE)) return;
    if (!row.canEdit) return;
    if (editingRowId.value === row.id) return;
    editingRowId.value = row.id;
  };

  const exitEditMode = () => {
    if (editingRowId.value === null) return;
    const row = tableData.value.find(
      (r: MCCSSettingsData) => r.id === editingRowId.value
    );
    if (!row) return;
    const errors = validateRow(row, tableData.value);
    if (Object.keys(errors).length > 0) {
      rowErrors.value[row.id] = errors;
    } else {
      delete rowErrors.value[row.id];
    }
    editingRowId.value = null;
  };

  const addNewRow = async () => {
    const newRowId = `new_${newRowCounter.value++}`;
    const newRow: MCCSSettingsData = {
      id: newRowId,
      property: '',
      type: 1, // Default to text
      description: null,
      toDelete: false,
      canEdit: true
    };
    store.commit('mccsProperties/setListData', [...tableData.value, newRow]);
    editingRowId.value = newRowId;
    await nextTick();
    setTimeout(() => {
      const lastRow = tableRef.value?.$el.querySelector(
        '.el-table__body-wrapper .el-table__row:last-child'
      );
      if (lastRow) {
        lastRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  const handleCreate = async (newRows: MCCSSettingsData[]) => {
    try {
      await store.dispatch('mccsProperties/create', newRows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errors = error.response?.data.errors;
      Object.keys(errors || {}).forEach((key) => {
        const rowError = errors[key];
        const rowId = newRows[Number(key)].id;
        rowErrors.value[rowId] = rowError;
      });
      throw error;
    }
  };

  const handleUpdate = async (rows: MCCSSettingsData[]) => {
    try {
      await store.dispatch('mccsProperties/update', rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errors = error.response?.data.errors;
      Object.keys(errors || {}).forEach((key) => {
        const rowError = errors[key];
        const rowId = rows[Number(key)].id;
        rowErrors.value[rowId] = rowError;
      });
      throw error;
    }
  };

  const updateSettings = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    try {
      // Determine rows to add, update, delete
      const toDeleteIds = tableData.value
        .filter((r: MCCSSettingsData) => r.toDelete && typeof r.id === 'number')
        .map((r: MCCSSettingsData) => r.id as number);
      const newRows = tableData.value.filter(
        (r: MCCSSettingsData) =>
          String(r.id).startsWith('new_') && !r.toDelete && r.property.trim()
      );
      const updateRows = tableData.value.filter(
        (r: MCCSSettingsData) => !String(r.id).startsWith('new_') && !r.toDelete
      );
      // Compare with originalData to only send changed rows
      const originalMap = Object.fromEntries(
        originalData.map((r: MCCSSettingsData) => [r.id, r])
      );
      const changedRows = updateRows.filter((r: MCCSSettingsData) => {
        const o = originalMap[r.id];
        return (
          o &&
          (o.property !== r.property ||
            o.type !== r.type ||
            o.description !== r.description)
        );
      });
      // Only call API if there are corresponding changes
      const promises = [];

      if (toDeleteIds.length)
        promises.push(store.dispatch('mccsProperties/delete', toDeleteIds));
      if (newRows.length) promises.push(handleCreate(newRows));
      if (changedRows.length) promises.push(handleUpdate(changedRows));

      if (promises.length) await Promise.all(promises);
      // Reload data
      await initialize();
      await nextTick();
      success('Success', 'MCCS設定が正常に更新されました');
      editingRowId.value = null;
    } catch (err) {
      error('400', '更新中にエラーが発生しました');
    } finally {
      loadingInstance.close();
    }
  };

  function setSelectRef(rowId: string | number) {
    return (
      el: InstanceType<(typeof import('element-plus'))['ElSelect']> | null
    ) => {
      if (el) selectRefs.value[rowId] = el;
      else delete selectRefs.value[rowId];
    };
  }

  function handleSelectVisibleChange(visible: boolean, rowId: string | number) {
    if (visible) {
      openSelectRowId.value = rowId;
    } else if (openSelectRowId.value === rowId) {
      openSelectRowId.value = null;
    }
  }

  function handleTableScroll() {
    if (openSelectRowId.value && selectRefs.value[openSelectRowId.value]) {
      selectRefs.value[openSelectRowId.value]?.blur();
      openSelectRowId.value = null;
    }
  }

  let scrollContainer: HTMLElement | null = null;

  // Detect click outside table to exit edit mode
  const handleClickOutside = () => {
    exitEditMode();
  };

  onMounted(() => {
    initialize();
    document.addEventListener('click', handleClickOutside);
    nextTick(() => {
      scrollContainer = tableRef.value?.$el?.querySelector(
        '.el-scrollbar__wrap'
      );
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleTableScroll);
      }
    });
  });
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', handleTableScroll);
    }
  });

  const tableRowClassName = ({ row }: { row: MCCSSettingsData }) => {
    return rowErrors.value[row.id] ? 'row-error' : '';
  };

  const handleDeleteChange = async (
    checked: boolean,
    row: MCCSSettingsData
  ) => {
    if (checked && !row.canEdit) {
      rowToDelete.value = row;
      deleteDialogVisible.value = true;
      row.toDelete = false;
    } else {
      row.toDelete = checked;
    }
  };

  const confirmDelete = async () => {
    if (!rowToDelete.value) return;

    rowToDelete.value.toDelete = true;
    deleteDialogVisible.value = false;
    rowToDelete.value = null;
  };

  const cancelDelete = () => {
    if (rowToDelete.value) {
      rowToDelete.value.toDelete = false;
    }
    deleteDialogVisible.value = false;
    rowToDelete.value = null;
  };
</script>

<style lang="scss">
  .mccs-settings {
    .is-error .el-input__wrapper {
      border-color: #f56c6c !important;
      background: #fff !important;
    }
    .el-input {
      width: 300px;
      margin: 0 20px;
    }

    .el-table {
      width: 100%;
      margin: 10px auto 0;

      thead th {
        background: #f5f7fa;
      }

      td {
        vertical-align: middle;
        padding: 8px;
      }

      .el-input {
        width: 100%;
        margin: 0;

        .el-input__wrapper {
          height: 32px;
          display: flex;
          align-items: center;
        }

        .el-input__inner {
          height: 32px;
          line-height: 32px;
        }
      }

      .el-select {
        width: 100%;

        .el-input__wrapper {
          height: 32px;
          display: flex;
          align-items: center;
        }

        .el-input__inner {
          height: 32px;
          line-height: 32px;
          text-align: left;
        }
      }

      .el-button {
        height: 32px;
        padding: 0 12px;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      tbody tr {
        cursor: pointer;

        &:hover {
          background-color: #f5f7fa;
        }
      }
    }

    .el-button {
      font-weight: 600;
    }

    .add-row-container {
      margin-top: 20px;
      text-align: center;
    }

    .mccs-settings-button-update {
      margin-left: 10px;
    }
  }
</style>
